import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ClipboardCheck, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LabelList
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { fetchChecklistHeatmap, fetchChecklistCriteria, fetchChecklistExamples } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import { getCallTypeOptionsForTenant, getQualityFormsForTenant } from '../config/endoTaxonomy';

const EXAMPLES_LIMIT = 10;
const tooltipWrapperStyle = { zIndex: 30 };
const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim().toLowerCase();
const PLUS_CHECKLIST_EXCLUDED_TOPICS = new Set(['all', 'no_answer_or_technical']);

// Адаптация resolveConversionStyle из EmployeeAnalytics под ячейки heatmap (красный → зелёный).
const resolveHeatmapCellClass = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'bg-muted text-muted-foreground';
  }

  const rate = Math.max(0, Math.min(100, value));

  if (rate < 20) return 'bg-red-100 text-red-800';
  if (rate < 40) return 'bg-orange-100 text-orange-800';
  if (rate < 60) return 'bg-yellow-100 text-yellow-800';
  if (rate < 80) return 'bg-lime-100 text-lime-800';
  return 'bg-green-100 text-green-800';
};

const formatScorePercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `${Number(value).toFixed(0)}%`;
};

const formatScoreValue = (score) => {
  if (score === null || score === undefined) return 'н/п';
  const numeric = Number(score);
  if (Number.isNaN(numeric)) return '—';
  if (numeric >= 1) return '1 — выполнено';
  if (numeric >= 0.5) return '0.5 — частично';
  return '0 — провал';
};

const formatExampleDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU');
};

const CriteriaTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  if (!item) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{item.label}</div>
      <div>Выполнение: {Number(item.avgScorePercent ?? 0).toFixed(1)}%</div>
      <div>Провалов: {Number(item.failedCount ?? 0).toLocaleString('ru-RU')}</div>
      {TENANT_ID !== 'plus' && (
        <div>Частично: {Number(item.partialCount ?? 0).toLocaleString('ru-RU')}</div>
      )}
      <div>Применимо: {Number(item.applicableCount ?? 0).toLocaleString('ru-RU')}</div>
    </div>
  );
};

const renderCriteriaBarLabel = (props) => {
  const { x, y, width, height, value } = props;
  if (value === null || value === undefined) {
    return null;
  }

  return (
    <text
      x={(x ?? 0) + (width ?? 0) + 6}
      y={(y ?? 0) + (height ?? 0) / 2}
      dominantBaseline="middle"
      fill="hsl(var(--foreground))"
      fontSize="11"
      fontWeight="600"
    >
      {value}
    </text>
  );
};

const ChecklistQualityBlock = ({
  dateRange,
  onOpenCall,
  selectedEmployees = ['all'],
  selectedDepartments = ['all'],
  selectedClassificationTypes = ['all']
}) => {
  const isPlusTenant = TENANT_ID === 'plus';
  const qualityForms = useMemo(() => getQualityFormsForTenant(TENANT_ID), []);
  const checklistTopicOptions = useMemo(
    () => isPlusTenant
      ? getCallTypeOptionsForTenant(TENANT_ID).filter(
        (option) => !PLUS_CHECKLIST_EXCLUDED_TOPICS.has(option.code)
      )
      : [],
    [isPlusTenant]
  );
  const [qualityForm, setQualityForm] = useState(qualityForms[0]?.code ?? 'bm_quality_checklist');
  const [selectedChecklistTopic, setSelectedChecklistTopic] = useState(() => {
    const inheritedTopic = selectedClassificationTypes.find(
      (type) => type !== 'all' && checklistTopicOptions.some((option) => option.code === type)
    );
    return inheritedTopic ?? checklistTopicOptions[0]?.code ?? 'all';
  });
  const [heatmap, setHeatmap] = useState(null);
  const [criteriaStats, setCriteriaStats] = useState([]);
  const [showCriteriaChart, setShowCriteriaChart] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [examples, setExamples] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const baseParams = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      return null;
    }

    const employeeIds = selectedEmployees.filter((value) => value !== 'all');
    const departments = selectedDepartments.filter((value) => value !== 'all');
    const callClassificationTypes = isPlusTenant
      ? [selectedChecklistTopic].filter((value) => value && value !== 'all')
      : selectedClassificationTypes.filter((value) => value !== 'all');

    return {
      date_from: format(dateRange.from, 'yyyy-MM-dd'),
      date_to: format(dateRange.to, 'yyyy-MM-dd'),
      quality_form: qualityForm,
      ...(employeeIds.length > 0 ? { employee_ids: employeeIds } : {}),
      ...(departments.length > 0 ? { departments } : {}),
      ...(callClassificationTypes.length > 0 ? { call_classification_types: callClassificationTypes } : {})
    };
  }, [
    dateRange?.from,
    dateRange?.to,
    qualityForm,
    isPlusTenant,
    selectedChecklistTopic,
    selectedEmployees,
    selectedDepartments,
    selectedClassificationTypes
  ]);

  useEffect(() => {
    if (!isPlusTenant) {
      return;
    }

    const inheritedTopics = selectedClassificationTypes.filter(
      (type) => type !== 'all' && checklistTopicOptions.some((option) => option.code === type)
    );
    if (inheritedTopics.length === 1) {
      setSelectedChecklistTopic(inheritedTopics[0]);
    }
  }, [checklistTopicOptions, isPlusTenant, selectedClassificationTypes]);

  const loadData = useCallback(async (showSpinner = true) => {
    if (!baseParams) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (showSpinner) {
      setIsLoading(true);
    }

    setError(null);
    try {
      const [heatmapData, criteriaData] = await Promise.all([
        fetchChecklistHeatmap(baseParams),
        fetchChecklistCriteria(baseParams)
      ]);
      if (requestId === requestIdRef.current) {
        setHeatmap(heatmapData ?? null);
        setCriteriaStats(Array.isArray(criteriaData) ? criteriaData : []);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setError('Не удалось загрузить данные чек-листа качества.');
      }
    } finally {
      if (showSpinner && requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [baseParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadData(false);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadData]);

  // При смене формы сбрасываем выбранную ячейку и примеры.
  useEffect(() => {
    setSelectedCell(null);
    setExamples([]);
  }, [qualityForm, selectedChecklistTopic, dateRange?.from, dateRange?.to, selectedEmployees, selectedDepartments, selectedClassificationTypes]);

  const criteria = useMemo(() => heatmap?.criteria ?? [], [heatmap]);

  const heatmapRows = useMemo(() => {
    const cells = heatmap?.cells ?? [];
    const byEmployee = new Map();

    cells.forEach((cell) => {
      const key = cell.employeeId ?? cell.employeeName ?? 'unknown';
      if (!byEmployee.has(key)) {
        byEmployee.set(key, {
          employeeId: cell.employeeId,
          employeeName: cell.employeeName ?? 'Без имени',
          cells: new Map()
        });
      }
      byEmployee.get(key).cells.set(cell.criterion, cell);
    });

    return Array.from(byEmployee.values())
      .map((row) => {
        const values = criteria
          .map((criterion) => row.cells.get(criterion.criterion)?.avgScorePercent)
          .filter((value) => value !== null && value !== undefined);
        const average = values.length
          ? values.reduce((acc, value) => acc + value, 0) / values.length
          : null;
        return { ...row, average };
      })
      .sort((a, b) => (a.employeeName ?? '').localeCompare(b.employeeName ?? '', 'ru', { sensitivity: 'base' }));
  }, [heatmap, criteria]);

  const criteriaChartData = useMemo(
    () =>
      (criteriaStats ?? []).map((item) => ({
        criterion: item.criterion,
        label: item.label ?? item.criterion,
        avgScorePercent: Number(item.avgScorePercent) || 0,
        failedCount: Number(item.failedCount) || 0,
        partialCount: Number(item.partialCount) || 0,
        applicableCount: Number(item.applicableCount) || 0,
        valueLabel: TENANT_ID === 'plus'
          ? `${(Number(item.avgScorePercent) || 0).toFixed(1)}% · провалов: ${Number(item.failedCount) || 0}`
          : `${(Number(item.avgScorePercent) || 0).toFixed(1)}% · провалов: ${Number(item.failedCount) || 0}, частично: ${Number(item.partialCount) || 0}`
      })),
    [criteriaStats]
  );

  const criteriaChartHeight = Math.max(240, criteriaChartData.length * 40 + 40);

  const loadExamples = useCallback(async (cell) => {
    if (!baseParams) {
      return;
    }

    setSelectedCell(cell);
    setIsLoadingExamples(true);
    try {
      const exampleParams = { ...baseParams };
      delete exampleParams.employee_ids;
      const data = await fetchChecklistExamples({
        ...exampleParams,
        criterion: cell.criterion,
        employee_id: cell.employeeId ?? undefined,
        max_score: TENANT_ID === 'plus' ? 0 : 0.5,
        limit: EXAMPLES_LIMIT
      });
      setExamples(Array.isArray(data) ? data : []);
    } catch {
      setExamples([]);
    } finally {
      setIsLoadingExamples(false);
    }
  }, [baseParams, qualityForm]);

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" />
              Чек-лист качества
            </CardTitle>
            <CardDescription>
              Выполнение критериев по операторам. Нажмите на ячейку, чтобы увидеть примеры провалов.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowCriteriaChart((prev) => !prev)}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            {showCriteriaChart ? 'Скрыть выполнение критериев' : 'Показать выполнение критериев'}
          </Button>
        </div>
        {qualityForms.length > 1 && (
        <div className="inline-flex flex-wrap rounded-md border">
          {qualityForms.map((form, index) => {
            const isActive = qualityForm === form.code;
            return (
              <button
                key={form.code}
                type="button"
                onClick={() => setQualityForm(form.code)}
                className={`px-3 py-1 text-sm ${index === 0 ? 'rounded-l-md' : ''} ${
                  index === qualityForms.length - 1 ? 'rounded-r-md' : ''
                } ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {form.label}
              </button>
            );
          })}
        </div>
        )}
        {isPlusTenant && checklistTopicOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Тема чек-листа</span>
            <Select value={selectedChecklistTopic} onValueChange={setSelectedChecklistTopic}>
              <SelectTrigger className="w-full sm:w-[320px]">
                <SelectValue placeholder="Выберите тему" />
              </SelectTrigger>
              <SelectContent>
                {checklistTopicOptions.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
        {!isLoading && heatmapRows.length === 0 && (
          <p className="text-sm text-muted-foreground">Нет данных чек-листа за выбранный период.</p>
        )}

        {heatmapRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 border-b bg-card p-2 text-left font-medium text-muted-foreground">
                    Оператор
                  </th>
                  {criteria.map((criterion) => (
                    <th
                      key={criterion.criterion}
                      title={criterion.label}
                      onClick={() => setShowCriteriaChart(true)}
                      className="max-w-[110px] cursor-pointer border-b p-2 text-center align-bottom font-medium text-muted-foreground hover:text-foreground"
                    >
                      <span className="line-clamp-3 break-words text-xs leading-tight">
                        {criterion.label}
                      </span>
                    </th>
                  ))}
                  <th className="border-b p-2 text-center font-medium text-muted-foreground">Средний</th>
                </tr>
              </thead>
              <tbody>
                {heatmapRows.map((row) => (
                  <tr key={row.employeeId ?? row.employeeName}>
                    <td className="sticky left-0 z-10 whitespace-nowrap border-b bg-card p-2 font-medium">
                      {row.employeeName}
                    </td>
                    {criteria.map((criterion) => {
                      const cell = row.cells.get(criterion.criterion);
                      if (!cell || cell.avgScorePercent === null || cell.avgScorePercent === undefined) {
                        return (
                          <td key={criterion.criterion} className="border-b p-1 text-center">
                            <span
                              className="inline-block w-full rounded bg-muted px-1 py-1 text-muted-foreground"
                              title={`${row.employeeName} · ${criterion.label}: не применимо`}
                            >
                              н/п
                            </span>
                          </td>
                        );
                      }

                      const isSelected =
                        selectedCell
                        && selectedCell.criterion === criterion.criterion
                        && (selectedCell.employeeId ?? selectedCell.employeeName) === (row.employeeId ?? row.employeeName);

                      return (
                        <td key={criterion.criterion} className="border-b p-1 text-center">
                          <button
                            type="button"
                            title={`${row.employeeName} · ${criterion.label}: ${formatScorePercent(cell.avgScorePercent)} (провалов: ${cell.failedCount ?? 0} из ${cell.applicableCount ?? 0})`}
                            onClick={() => loadExamples({
                              employeeId: row.employeeId,
                              employeeName: row.employeeName,
                              criterion: criterion.criterion,
                              criterionLabel: criterion.label
                            })}
                            className={`w-full rounded px-1 py-1 font-medium transition-shadow hover:shadow ${resolveHeatmapCellClass(cell.avgScorePercent)} ${isSelected ? 'ring-2 ring-ring' : ''}`}
                          >
                            {formatScorePercent(cell.avgScorePercent)}
                          </button>
                        </td>
                      );
                    })}
                    <td className="border-b p-1 text-center">
                      <span className={`inline-block w-full rounded px-1 py-1 font-semibold ${resolveHeatmapCellClass(row.average)}`}>
                        {formatScorePercent(row.average)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showCriteriaChart && (
          <div className="space-y-2 rounded-md border p-3">
            <h4 className="text-sm font-semibold">Выполнение критериев</h4>
            {criteriaChartData.length === 0 ? (
              <p className="text-sm text-muted-foreground">Нет данных по критериям.</p>
            ) : (
              <div className="w-full min-w-0 rounded-md bg-muted/30 px-2 py-4" style={{ height: criteriaChartHeight }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                  <BarChart
                    data={criteriaChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 220, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={190}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <RechartsTooltip
                      wrapperStyle={tooltipWrapperStyle}
                      cursor={{ fill: 'rgba(148,163,184,0.15)' }}
                      content={<CriteriaTooltip />}
                    />
                    <Bar dataKey="avgScorePercent" name="Выполнение" fill="#2563eb" radius={[0, 4, 4, 0]} maxBarSize={28}>
                      <LabelList dataKey="valueLabel" content={renderCriteriaBarLabel} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {selectedCell && (
          <div className="space-y-3 rounded-md border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">
                Примеры: {selectedCell.employeeName} · {selectedCell.criterionLabel}
              </h4>
              <Button size="sm" variant="ghost" onClick={() => { setSelectedCell(null); setExamples([]); }}>
                Скрыть
              </Button>
            </div>
            {isLoadingExamples && <p className="text-sm text-muted-foreground">Загрузка примеров...</p>}
            {!isLoadingExamples && examples.length === 0 && (
              <p className="text-sm text-muted-foreground">Провалов и частичных выполнений не найдено.</p>
            )}
            {!isLoadingExamples && examples.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Дата</TableHead>
                      <TableHead>Оператор</TableHead>
                      <TableHead>Критерий</TableHead>
                      <TableHead>Оценка</TableHead>
                      <TableHead>Причина</TableHead>
                      <TableHead>Фрагмент</TableHead>
                      <TableHead>Краткое содержание</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {examples.map((example, index) => (
                      <TableRow key={`${example.callId}-${index}`}>
                        <TableCell className="whitespace-nowrap">{formatExampleDate(example.callStartedAt)}</TableCell>
                        <TableCell>{example.employeeName ?? '—'}</TableCell>
                        <TableCell>{example.criterionLabel ?? example.criterion}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatScoreValue(example.score)}</TableCell>
                        <TableCell className="max-w-[240px]">
                          <span className="line-clamp-3">{example.reason ?? '—'}</span>
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          <span className="line-clamp-3 italic text-muted-foreground">
                            {example.evidence ? `«${example.evidence}»` : '—'}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[280px]">
                          <span className="line-clamp-3">{example.summary ?? '—'}</span>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => onOpenCall?.(example.callId)}>
                            Открыть звонок
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChecklistQualityBlock;
